import * as React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton, { iconButtonClasses } from "@mui/joy/IconButton";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import Typography from "@mui/joy/Typography";
import Tooltip from "@mui/joy/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import { Link } from "react-router-dom";
import { ColorPaletteProp } from "@mui/joy/styles";
import Chip from "@mui/joy/Chip";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

interface Props {
  data: any;
  rowsPerPage: number;
}

type SeverityType = "Low" | "Medium" | "High" | "Critical";

const PaginationTable: React.FC<Props> = ({ data, rowsPerPage }) => {
  const [currentPage, setCurrentPage] = React.useState(0);
  const [selectedEventId, setSelectedEventId] = React.useState<string | null>(
    null
  );

  const [openRows, setOpenRows] = React.useState<Set<number>>(new Set());

  const SeverityColors: { [key in SeverityType]: ColorPaletteProp } = {
    Low: "success",
    Medium: "primary",
    High: "warning",
    Critical: "danger",
  };

  const groupedData = React.useMemo(() => {
    const groups: Record<
      string,
      { email: string; userName: string; eventName: string; reports: any[] }
    > = {};

    data.forEach((item: any) => {
      const groupKey = `${item.submittedBy}-${item.eventName}`;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          email: item.submittedBy,
          userName: item.submittedByName,
          eventName: item.eventName,
          reports: [],
        };
      }
      groups[groupKey].reports.push(item);
    });

    return Object.values(groups);
  }, [data]);

  const totalPages = Math.ceil(groupedData.length / rowsPerPage);

  const currentRows = React.useMemo(() => {
    const indexOfLastRow = (currentPage + 1) * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    return groupedData.slice(indexOfFirstRow, indexOfLastRow);
  }, [groupedData, currentPage, rowsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowToggle = (index: number) => {
    setOpenRows((prevOpenRows) => {
      const newOpenRows = new Set(prevOpenRows);
      if (newOpenRows.has(index)) {
        newOpenRows.delete(index);
      } else {
        newOpenRows.add(index);
      }
      return newOpenRows;
    });
  };

  return (
    <>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        <Table
          aria-labelledby="Evaluation Table"
          stickyHeader
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  width: "80px",
                  textAlign: "center",
                  padding: "12px 6px",
                }}
              >
                Sr. No
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Event Name
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                User Name
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                User Email
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Submissions
              </th>
              <th
                style={{
                  textAlign: "center",
                  padding: "12px 6px",
                  width: "50px",
                }}
              />
            </tr>
          </thead>
          <tbody>
            {currentRows.map((group, index) => (
              <React.Fragment key={`${group.email}-${group.eventName}`}>
                <tr>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">
                      {index + 1 + currentPage * rowsPerPage}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">
                      {group.eventName ?? "None"}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    {group.userName}
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    {group.email}
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    {group.reports.length}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <IconButton
                      aria-label="expand row"
                      variant="plain"
                      color="neutral"
                      size="sm"
                      onClick={() => handleRowToggle(index)}
                    >
                      {openRows.has(index) ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </td>
                </tr>
                {openRows.has(index) && (
                  <tr>
                    <td style={{ height: 0, padding: 0 }} colSpan={6}>
                      <Sheet
                        variant="soft"
                        sx={{
                          p: 1,
                          pl: 6,
                          boxShadow: "inset 0 3px 6px 0 rgba(0 0 0 / 0.08)",
                        }}
                      >
                        <Typography level="title-lg" component="div">
                          Report Details
                        </Typography>
                        <Table
                          borderAxis="bothBetween"
                          size="sm"
                          aria-label="report details"
                          sx={{
                            "& > thead > tr > th:nth-of-type(n + 3), & > tbody > tr > td:nth-of-type(n + 3)":
                              {
                                textAlign: "right",
                              },
                            "--TableCell-paddingX": "0.5rem",
                          }}
                        >
                          <thead>
                            <tr>
                              <th style={{ textAlign: "center" }}>
                                Report Title
                              </th>
                              <th style={{ textAlign: "center" }}>
                                Vulnerability Type
                              </th>
                              <th style={{ textAlign: "center" }}>Severity</th>
                              <th style={{ textAlign: "center" }}>Score</th>
                              <th style={{ textAlign: "center" }}>Info</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.reports.map((report: any) => (
                              <tr key={report.id}>
                                <td style={{ textAlign: "center" }}>
                                  {report.reporttitle || "N/A"}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {report.vulnerabilitytype || "N/A"}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  <Chip
                                    variant="soft"
                                    size="sm"
                                    color={
                                      SeverityColors[
                                        report.severitylevel as SeverityType
                                      ] || "neutral"
                                    }
                                  >
                                    {report.severitylevel || "N/A"}
                                  </Chip>
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  {report.score !== undefined
                                    ? report.score
                                    : "N/A"}
                                </td>
                                <td style={{ textAlign: "center" }}>
                                  <Tooltip
                                    title="View Details"
                                    color="success"
                                    placement="bottom"
                                    variant="solid"
                                  >
                                    <IconButton
                                      color="success"
                                      size="sm"
                                      variant="plain"
                                      component={Link}
                                      to={`/evaluatereport/${report.id}`}
                                    >
                                      <InfoIcon />
                                    </IconButton>
                                  </Tooltip>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Sheet>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </Sheet>

      <Box
        className="Pagination-laptopUp"
        sx={{
          pt: 2,
          gap: 1,
          [`& .${iconButtonClasses.root}`]: { borderRadius: "50%" },
          display: {
            xs: "none",
            md: "flex",
          },
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={currentPage === 0}
          onClick={() => handlePageChange(currentPage - 1)}
          startDecorator={<KeyboardArrowLeftIcon />}
        >
          Previous
        </Button>

        {Array.from({ length: totalPages }).map((_, index) => (
          <IconButton
            key={index}
            size="sm"
            variant={currentPage === index ? "outlined" : "plain"}
            color="neutral"
            onClick={() => handlePageChange(index)}
          >
            {index + 1}
          </IconButton>
        ))}

        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={currentPage === totalPages - 1}
          onClick={() => handlePageChange(currentPage + 1)}
          endDecorator={<KeyboardArrowRightIcon />}
        >
          Next
        </Button>
      </Box>
    </>
  );
};

export default PaginationTable;
